/**
 * /meetings/:id/edit — meeting editor.
 *
 * Week 3 scope: paste → transform flow lit up. Rich editable preview (Tiptap
 * bidirectional sync) lands in the next commit; for now the preview is
 * read-only and any edits to raw_input trigger a new transform.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, downloadFile, postAndStream } from '../lib/apiClient.ts';
import { NO_AUTOFILL } from '../lib/noAutofill.ts';
import { useAutoSave } from '../hooks/useAutoSave.ts';
import { EditableStructuredPreview } from '../components/editor/EditableStructuredPreview.tsx';
import { ScaledPaper } from '../components/editor/ScaledPaper.tsx';
import {
  EditorSplitPane,
  type EditorSplitPaneHandle,
} from '../components/editor/EditorSplitPane.tsx';
import {
  DocumentStatsBadge,
  PageNumbersToggle,
} from '../components/editor/PaperBadges.tsx';
import { PaperSkeleton } from '../components/editor/PaperSkeleton.tsx';
import { TiptapStructuredEditor } from '../components/editor/TiptapStructuredEditor.tsx';
import {
  StructuredEditorErrorBoundary,
  installGlobalErrorListeners,
} from '../components/editor/StructuredEditorErrorBoundary.tsx';

// Install global window.error + unhandledrejection listeners on
// first import so async crashes from any route surface as a fixed
// banner with a Reload button instead of a silent blank screen.
installGlobalErrorListeners();
import { useAuth } from '../context/AuthContext.tsx';
import { useFeatureFlag } from '../hooks/useFeatureFlag.ts';
import { AttendeesPanel } from '../components/editor/AttendeesPanel.tsx';
import { TokenMeter } from '../components/editor/TokenMeter.tsx';
import {
  TransformProgressBar,
  phaseBaseProgress,
  writingProgressFromBytes,
  type TransformPhase,
} from '../components/editor/TransformProgressBar.tsx';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/ui/Modal.tsx';
import {
  ChevronDown,
  Mail,
  Sparkles,
  Unlock,
  X,
} from 'lucide-react';
import { AiEnhancements } from '../components/editor/AiEnhancements.tsx';
import { RewriteBubble } from '../components/editor/RewriteBubble.tsx';
import { SendEmailDialog } from '../components/editor/SendEmailDialog.tsx';
import {
  MergeOrFinalizeDialog,
  type DuplicateCandidate,
} from '../components/editor/MergeOrFinalizeDialog.tsx';
import {
  FollowupSuggestionDialog,
  type FollowupDetection,
} from '../components/editor/FollowupSuggestionDialog.tsx';
import { FollowupPromptDialog } from '../components/editor/FollowupPromptDialog.tsx';
import { SaveToFolderToast } from '../components/editor/SaveToFolderToast.tsx';
import { estimateTokens, TOKEN_THRESHOLDS } from '../lib/tokenCount.ts';
import { replaceFirstSnippet } from '../lib/structuredContentHelpers.ts';
import { useTour } from '../lib/useTour.ts';
import type { StructuredContent } from '@ojb/shared';

interface Meeting {
  id: string;
  title: string;
  meetingDate: string;
  notesDate: string;
  projectId: string;
  /** Display-only override for the project name shown in the
   *  rendered minutes. NULL means "use the real project name".
   *  Edits via the editor's Meeting Details panel PATCH this field
   *  (and the server mirrors the value into structuredContent.project
   *  so the right-pane preview updates without re-running transform). */
  projectAlias: string | null;
  meetingFormat: 'in_person' | 'video' | 'phone' | 'hybrid' | null;
  location: string | null;
  status: 'draft' | 'finalized' | 'soft_deleted';
  inputSource: 'transcript' | 'ai_summary' | 'freeform';
  templateVariant: 'client_meeting' | 'internal';
  rawInput: string;
  structuredContent: Record<string, unknown>;
  /** When set, the current structured_content is a translation and this
   *  is the snapshot from before it ran. Non-null ↔ "revert" is available. */
  preTranslateContent?: Record<string, unknown> | null;
  /** Language most recently translated to (e.g. "Spanish"). */
  preTranslateLanguage?: string | null;
  /** Attendees captured at creation via calendar prefill or manual
   *  entry. Email is optional — externals can be added by name only.
   *  Email-dependent UI (Send-email dialog To/CC, follow-up invite
   *  recipients) filters out entries where email is missing. */
  attendeeEmails?: Array<{ name: string; email?: string; isOjb: boolean }> | null;
  version: number;
  updatedAt: string;
  finalizedAt: string | null;
}

interface TransformResult {
  meeting: Meeting;
  usage: {
    costUsd: number;
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    durationMs: number;
  };
  /** Glossary terms that appeared in the raw input and were passed to
   *  Claude for expansion-on-first-use. Empty array if none matched. */
  glossaryMatches?: Array<{ term: string; expansion: string }>;
  /** Prior finalized meetings for the same project that were injected
   *  as project memory. Empty array if none. Shown in the editor as a
   *  small "Drew on N prior meetings" note for transparency. */
  priorMeetingsUsed?: Array<{
    id: string;
    title: string;
    meetingDate: string;
    templateVariant: string;
  }>;
}

/**
 * EditorRoute — wraps the inner route in a route-level error
 * boundary so a render-time crash anywhere inside (Tiptap,
 * dialogs, header, autosave indicator, …) shows the recoverable
 * card instead of unmounting and leaving a blank page. The
 * narrow boundary around just the Tiptap editor (still in place
 * inside) catches the common case earlier; this outer layer
 * catches everything that escapes it.
 */
export function EditorRoute() {
  return (
    <StructuredEditorErrorBoundary>
      <EditorRouteInner />
    </StructuredEditorErrorBoundary>
  );
}

function EditorRouteInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const meeting = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => api.get<Meeting>(`/meetings/${id}`),
    enabled: !!id,
  });

  // Ref on the paper container so the rewrite bubble knows where selections
  // are valid (only inside the document, not in the form).
  const paperContainerRef = useRef<HTMLDivElement>(null);

  // Send-email dialog open state — kept here so the editor header button
  // controls it and the dialog mounts under the editor tree.
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  // ── Feature flag: Tiptap rebuild. Default ON after Phase 6 —
  //    every user lands on the new free-flow editor unless they
  //    explicitly opt out via ?tiptap=0 (writes through to
  //    localStorage so the choice sticks across nav). The legacy
  //    schema-bound editor stays available as a fallback during
  //    pilot until we delete the EditableStructuredPreview path.
  const useTiptap = useFeatureFlag('tiptap', true);

  // Phase 5: page count surfaced from the Tiptap editor's
  // PaginatedPaper. Lives here so DocumentStatsBadge in the
  // ScaledPaper toolbar can show it. Legacy path leaves it at 0
  // so the badge omits the "X pages" stat.
  const [tiptapPageCount, setTiptapPageCount] = useState(0);

  // ── SplitPane imperative ref — used by ScaledPaper's onZoomIn
  //    to nudge the form pane smaller when the user explicitly
  //    enlarges the paper. Zoom-out leaves splitter alone.
  const splitRef = useRef<EditorSplitPaneHandle>(null);

  // ── Smart paste detection — when the user pastes anything into
  //    the raw input, surface a small "Transform?" pill near the
  //    button. Auto-dismiss after 8s, on next user keypress, or on
  //    button click. Triggers regardless of paste size per pilot
  //    pref ("always offer, even on small pastes"). ──────────────
  const [showPasteOffer, setShowPasteOffer] = useState(false);
  const pasteOfferTimerRef = useRef<number | null>(null);
  const onRawInputPaste = useCallback(() => {
    setShowPasteOffer(true);
    if (pasteOfferTimerRef.current !== null) {
      window.clearTimeout(pasteOfferTimerRef.current);
    }
    pasteOfferTimerRef.current = window.setTimeout(() => {
      setShowPasteOffer(false);
      pasteOfferTimerRef.current = null;
    }, 8000);
  }, []);
  const dismissPasteOffer = useCallback(() => {
    setShowPasteOffer(false);
    if (pasteOfferTimerRef.current !== null) {
      window.clearTimeout(pasteOfferTimerRef.current);
      pasteOfferTimerRef.current = null;
    }
  }, []);

  // ── Page numbers toggle (preview chrome). Persisted in
  //    localStorage so each user's preference sticks across editor
  //    sessions on this device. Default on. NOT yet wired into
  //    .docx export — that still always shows page numbers; if
  //    pilot users want to honor the toggle in export too, we'd
  //    add a meetings.show_page_numbers field. ────────────────────
  const [showPageNumbers, setShowPageNumbers] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem('editor.showPageNumbers');
    return stored === null ? true : stored === '1';
  });
  const togglePageNumbers = useCallback(() => {
    setShowPageNumbers((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('editor.showPageNumbers', next ? '1' : '0');
      } catch {
        /* localStorage may be blocked; preference is best-effort */
      }
      return next;
    });
  }, []);

  // ── Split-pane preference (left form / right paper width) ───────
  // Default biases toward paper (35% form, 65% paper) per pilot
  // review — paper is the primary view.
  const { user } = useAuth();
  const splitPercent = user?.editorSplitPercent ?? 35;
  // Debounced PATCH so dragging fast doesn't fire one request per
  // pixel. Fire-and-forget — we deliberately do NOT call
  // refreshUser() afterwards. Refreshing the auth user would change
  // our `splitPercent` prop mid-drag, which would re-sync the
  // SplitPane's local state and cause a visible flash. Persistence
  // works on the next mount via the GET /me fetch.
  const splitCommitTimeoutRef = useRef<number | null>(null);
  const persistSplitPercent = useCallback((next: number) => {
    if (splitCommitTimeoutRef.current !== null) {
      window.clearTimeout(splitCommitTimeoutRef.current);
    }
    splitCommitTimeoutRef.current = window.setTimeout(() => {
      api
        .patch<{ editorSplitPercent: number }>('/me', {
          editorSplitPercent: next,
        })
        .catch(() => {
          /* swallow — split is a UI nicety, not critical state */
        });
    }, 350);
  }, []);

  // Merge-or-finalize dialog state. Populated by handleFinalize when the
  // server returns a 409 with fuzzy duplicate candidates.
  const [mergeDialog, setMergeDialog] = useState<{
    open: boolean;
    duplicates: DuplicateCandidate[];
  }>({ open: false, duplicates: [] });

  // Follow-up-invite suggestion flow. Post-finalize/email, Claude
  // classifies any follow-up discussion as 'none' | 'vague' | 'specific'.
  // UI branches:
  //   'none'     → nothing happens
  //   'vague'    → show a small yes/no prompt; if user says yes,
  //                transition to the full form (empty date)
  //   'specific' → open the full form directly, prefilled with the
  //                detected date / title / attendees
  const [followupDialog, setFollowupDialog] = useState<{
    mode: 'closed' | 'prompt' | 'form';
    detection: FollowupDetection | null;
  }>({ mode: 'closed', detection: null });
  // Tracks whether we've already fired detection for this meeting in the
  // current session. Avoids hitting Claude twice if the user downloads
  // the .eml and then exports a PDF back-to-back.
  const followupCheckedRef = useRef(false);

  // "Save to the project management folder" reminder toast. Fires once
  // per editor mount on the first finalize OR email event — whichever
  // comes first. Email auto-finalizes, so firing on both would double-
  // prompt; guarding with a ref avoids that.
  const [saveReminderOpen, setSaveReminderOpen] = useState(false);
  const saveReminderFiredRef = useRef(false);
  function maybeShowSaveReminder() {
    if (saveReminderFiredRef.current) return;
    saveReminderFiredRef.current = true;
    setSaveReminderOpen(true);
  }

  // Local draft for metadata + raw input + structured content
  const [draft, setDraft] = useState<{
    title: string;
    location: string;
    meetingDate: string;
    notesDate: string;
    meetingFormat: 'in_person' | 'video' | 'phone' | 'hybrid' | null;
    /** Display-only project name override. Empty string clears it
     *  back to "use the real project name". The autosave hook
     *  PATCHes this and the server mirrors the value into
     *  structuredContent.project so the right-pane preview updates
     *  without a re-transform. */
    projectAlias: string;
    rawInput: string;
    templateVariant: 'client_meeting' | 'internal';
    structuredContent: StructuredContent | null;
  } | null>(null);

  useEffect(() => {
    if (meeting.data && !draft) {
      setDraft({
        title: meeting.data.title,
        location: meeting.data.location ?? '',
        meetingDate: meeting.data.meetingDate,
        notesDate: meeting.data.notesDate,
        meetingFormat: meeting.data.meetingFormat,
        projectAlias: meeting.data.projectAlias ?? '',
        rawInput: meeting.data.rawInput,
        templateVariant:
          meeting.data.templateVariant === 'internal' ? 'internal' : 'client_meeting',
        structuredContent: isStructuredContent(meeting.data.structuredContent)
          ? (meeting.data.structuredContent as StructuredContent)
          : null,
      });
    }
  }, [meeting.data, draft]);

  // Optimistic concurrency: pass the last-known server version so the
  // server can reject silent two-tab clobbers with a 409. On save
  // success we update the cached meeting with the returned row (which
  // carries the bumped version) so the next save sends the right
  // expectedVersion.
  // Fallback `null` (not `{}`) — useAutoSave skips the first render
  // anyway, but the value type expects either the full Draft shape
  // OR null. Passing an empty literal would have been silent type
  // pollution if the hook ever started reading fields off it.
  const autoSaveStatus = useAutoSave<typeof draft, Meeting>(id, draft ?? null, {
    version: meeting.data?.version,
    onSaved: (updated) => {
      if (!id) return;
      qc.setQueryData<Meeting>(['meeting', id], updated);
      // INTENTIONALLY no setDraft here. An earlier version of this
      // callback mirrored updated.structuredContent.project back into
      // draft.structuredContent so the right-pane preview would
      // reflect server-side mirroring of the project alias. That
      // worked but caused the editor to crash with "removeChild on
      // Node — not a child of this node": every alias-driven autosave
      // mutated draft.structuredContent, which triggered
      // TiptapStructuredEditor's external-content-sync useEffect, which
      // called editor.commands.setContent() to re-seed the entire
      // ProseMirror doc. The setContent call collided with React's
      // DOM reconciliation and the editor crashed.
      //
      // Trade-off: the right-pane preview's project line now does NOT
      // update on alias edits without a page reload. The alias IS
      // saved correctly (next reload reflects it; PDF/DOCX exports
      // pick up the alias too), but the live preview lags. A proper
      // fix uses editor.commands.updateAttributes('meetingHeader',
      // { project: newValue }) for a surgical single-attr update
      // without re-seeding — that's a follow-up that requires
      // exposing the editor instance from TiptapStructuredEditor.
    },
  });
  const rawInputTokens = estimateTokens(draft?.rawInput ?? '');
  const overLimit = rawInputTokens >= TOKEN_THRESHOLDS.HARD_CAP;

  /**
   * Title, meeting date, and location live in BOTH draft.* (used by the form
   * + header display + meetings row on the server) AND draft.structuredContent.*
   * (used by the document preview + saved into structured_content JSONB).
   * These helpers keep them in lockstep so edits in one view reflect in the
   * other.
   */
  function updateTitle(title: string) {
    setDraft((d) =>
      !d
        ? d
        : {
            ...d,
            title,
            structuredContent: d.structuredContent
              ? { ...d.structuredContent, title }
              : d.structuredContent,
          },
    );
  }
  function updateLocation(location: string) {
    setDraft((d) =>
      !d
        ? d
        : {
            ...d,
            location,
            structuredContent: d.structuredContent
              ? { ...d.structuredContent, location }
              : d.structuredContent,
          },
    );
  }
  function updateMeetingDate(meetingDate: string) {
    setDraft((d) =>
      !d
        ? d
        : {
            ...d,
            meetingDate,
            structuredContent: d.structuredContent
              ? { ...d.structuredContent, meetingDate }
              : d.structuredContent,
          },
    );
  }
  function updateNotesDate(notesDate: string) {
    setDraft((d) =>
      !d
        ? d
        : {
            ...d,
            notesDate,
            structuredContent: d.structuredContent
              ? { ...d.structuredContent, notesDate }
              : d.structuredContent,
          },
    );
  }
  function updateMeetingFormat(
    meetingFormat: 'in_person' | 'video' | 'phone' | 'hybrid' | null,
  ) {
    setDraft((d) =>
      !d
        ? d
        : {
            ...d,
            meetingFormat,
            structuredContent: d.structuredContent
              ? {
                  ...d.structuredContent,
                  meetingFormat: meetingFormat ?? d.structuredContent.meetingFormat,
                }
              : d.structuredContent,
          },
    );
  }

  // Streaming transform state — phase + progress drive the pine
  // progress bar. Monotonic (never decreases) via maxProgressRef so
  // delta jitter can't visually rewind the bar.
  const [transformPhase, setTransformPhase] =
    useState<TransformPhase>('analyzing');
  const [transformProgress, setTransformProgress] = useState(0);
  const maxTransformProgressRef = useRef(0);
  const bumpTransformProgress = (next: number) => {
    const clamped = Math.max(
      maxTransformProgressRef.current,
      Math.min(100, next),
    );
    maxTransformProgressRef.current = clamped;
    setTransformProgress(clamped);
  };

  const transformMutation = useMutation<
    { result: TransformResult | null },
    Error,
    void
  >({
    mutationFn: async () => {
      if (!id) throw new Error('No meeting id');
      maxTransformProgressRef.current = 0;
      setTransformProgress(0);
      setTransformPhase('analyzing');

      let result: TransformResult | null = null;

      await postAndStream(
        `/meetings/${id}/transform-stream`,
        { rawInput: draft?.rawInput },
        (eventName, data) => {
          switch (eventName) {
            case 'analyzing':
              setTransformPhase('analyzing');
              bumpTransformProgress(phaseBaseProgress('analyzing'));
              break;
            case 'writing':
              setTransformPhase('writing');
              bumpTransformProgress(phaseBaseProgress('writing'));
              break;
            case 'delta': {
              const bytes =
                typeof data === 'object' && data !== null && 'bytes' in data
                  ? Number((data as { bytes: unknown }).bytes) || 0
                  : 0;
              bumpTransformProgress(writingProgressFromBytes(bytes));
              break;
            }
            case 'validating':
              setTransformPhase('validating');
              bumpTransformProgress(phaseBaseProgress('validating'));
              break;
            case 'result':
              // The server's final event carries the full
              // TransformResult-shaped payload; stash it for onSuccess.
              result = data as TransformResult;
              break;
            case 'done':
              setTransformPhase('done');
              bumpTransformProgress(100);
              break;
            case 'error': {
              const message =
                typeof data === 'object' && data !== null && 'message' in data
                  ? String((data as { message: unknown }).message)
                  : 'Transform failed';
              throw new Error(message);
            }
            default:
              // Unknown events are ignored so the server can add new
              // ones without breaking older clients.
              break;
          }
        },
      );

      return { result };
    },
    onSuccess: ({ result }) => {
      if (!result) {
        // Stream completed without a 'result' event — the server's
        // onError path may have fired but somehow we still landed
        // here. Log it and bail so we don't write `undefined` into
        // the React Query cache (which would blank consumers).
        console.warn(
          '[EditorRoute] transform stream completed with no result payload',
        );
        return;
      }
      try {
        // Defensive merge: preserve any existing fields from the
        // cached meeting that the streaming `result.meeting`
        // doesn't include. Drizzle's .returning() generally gives
        // a full row but I want to belt-and-suspenders this so a
        // missing field can't blank a downstream consumer.
        const previous = qc.getQueryData(['meeting', id]) as
          | Partial<Meeting>
          | undefined;
        const merged = { ...(previous ?? {}), ...(result.meeting ?? {}) } as Meeting;
        qc.setQueryData(['meeting', id], merged);

        if (draft && isStructuredContent(result.meeting.structuredContent)) {
          setDraft({
            ...draft,
            structuredContent:
              result.meeting.structuredContent as StructuredContent,
          });
        } else {
          console.warn(
            '[EditorRoute] re-transform result.meeting.structuredContent failed isStructuredContent check; keeping previous draft.structuredContent',
            { received: result.meeting.structuredContent },
          );
        }

        // 4e.iv — auto-suggest a title when the first transform
        // lands on a meeting that still has a weak or empty title.
        const t = (result.meeting.title ?? '').trim();
        const isWeak =
          t.length === 0 ||
          /^(untitled|meeting|new meeting|draft)$/i.test(t);
        if (isWeak) {
          void autoSuggestTitle();
        }
      } catch (err) {
        // Anything thrown from the cache update or downstream React
        // Query subscribers gets caught here so the user sees a
        // banner via the global error listener instead of a blank
        // screen. Re-throw so the listener picks it up.
        console.error(
          '[EditorRoute] transform onSuccess threw - surfacing globally',
          err,
        );
        throw err;
      }
    },
  });

  /**
   * Auto-suggest a title after the first transform when the user
   * hasn't filled one in. Calls /suggest-title, previews the result
   * as a small banner with Accept/Dismiss. Failure is silent — a
   * missed suggestion is better than a surprise modal.
   */
  const [suggestedTitle, setSuggestedTitle] = useState<string | null>(null);

  // Raw input section collapse state (4d.i). Default collapsed once
  // structuredContent exists — the post-transform user spends their
  // time in the paper pane, not the raw input. Opens via a disclosure
  // button, and whenever a transform error happens so the user can
  // see what they pasted.
  const [rawInputOpen, setRawInputOpen] = useState<boolean | null>(null);
  const effectiveRawInputOpen =
    rawInputOpen !== null
      ? rawInputOpen
      : !draft?.structuredContent;

  // Supplemental input — lets the user paste a SECOND chunk of
  // raw content after the draft already exists (e.g. an addendum,
  // a chat thread that arrived later, pre-meeting agenda notes
  // that should also feed the next Re-transform). On Append we
  // concatenate the supplemental content onto draft.rawInput
  // with a dated separator so Claude can see it as a discrete
  // section on the next pass.
  const [supplementalOpen, setSupplementalOpen] = useState(false);
  const [supplementalDraft, setSupplementalDraft] = useState('');
  const [supplementalLabel, setSupplementalLabel] = useState('');

  const appendSupplementalInput = () => {
    if (!draft) return;
    const body = supplementalDraft.trim();
    if (!body) return;
    const labelTrim = supplementalLabel.trim();
    const today = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    // Plain-text separator that's visible to humans AND parses
    // cleanly as a labeled section when Claude reads the
    // combined input on the next transform. The blank line
    // before/after the dashes gives generous breathing room
    // when the user inspects the textarea.
    const heading = labelTrim ? `${labelTrim.toUpperCase()} - ${today}` : `ADDITIONAL INPUT - ${today}`;
    const separator = `\n\n--- ${heading} ---\n\n`;
    const existing = draft.rawInput ?? '';
    const next = existing.trimEnd() + separator + body + '\n';
    setDraft({ ...draft, rawInput: next });
    setSupplementalDraft('');
    setSupplementalLabel('');
    setSupplementalOpen(false);
    // Make sure the user can see the appended content right away
    // — open the raw input panel if it was collapsed.
    setRawInputOpen(true);
  };

  // Re-transform confirmation (4d.ii) — when structured content
  // already exists, hitting Transform opens a "this will overwrite"
  // modal before firing the mutation. First transform (no structured
  // content yet) fires directly.
  const [confirmRetransformOpen, setConfirmRetransformOpen] = useState(false);
  function handleTransformClick() {
    if (draft?.structuredContent) {
      setConfirmRetransformOpen(true);
    } else {
      transformMutation.mutate();
    }
  }

  async function autoSuggestTitle() {
    if (!id) return;
    try {
      const res = await api.post<{ title: string }>(
        `/meetings/${id}/suggest-title`,
        {},
      );
      if (res?.title?.trim()) {
        setSuggestedTitle(res.title.trim());
      }
    } catch {
      // ignore — not worth surfacing
    }
  }

  /**
   * Ask the API whether a follow-up meeting was discussed in the
   * source text, classify how specific it was, and open the matching
   * UI. No-op if we've already checked this session, if there's no
   * meeting id, or if status is 'none'. Errors are swallowed — a
   * missed suggestion is far less annoying than a surprise modal
   * complaining about Claude being unavailable.
   *
   * Flow by status:
   *   'none'     → do nothing
   *   'vague'    → open prompt dialog (yes/no)
   *   'specific' → open form dialog directly (date prefilled)
   */
  async function maybeSuggestFollowup() {
    if (!id || followupCheckedRef.current) return;
    followupCheckedRef.current = true;
    try {
      const res = await api.post<{
        status: 'none' | 'vague' | 'specific';
        proposedDate?: string;
        proposedTitle?: string;
        proposedAttendees?: string[];
        evidenceQuote?: string;
      }>(`/meetings/${id}/detect-followup`, {});

      if (res.status === 'none') return;

      const detection: FollowupDetection = {
        status: res.status,
        proposedDate: res.proposedDate,
        proposedTitle: res.proposedTitle,
        proposedAttendees: res.proposedAttendees,
        evidenceQuote: res.evidenceQuote,
      };

      setFollowupDialog({
        mode: res.status === 'specific' ? 'form' : 'prompt',
        detection,
      });
    } catch {
      // Silent — detection is a nice-to-have, not a blocking flow.
    }
  }

  async function handleFinalize() {
    if (!id) return;
    try {
      await api.post(`/meetings/${id}/finalize`);
      qc.invalidateQueries({ queryKey: ['meeting', id] });
      qc.invalidateQueries({ queryKey: ['meetings'] });
      // Fire the follow-up detector in the background — runs after the
      // finalize completes so the UI has already flipped to "finalized".
      void maybeSuggestFollowup();
      // Nudge to save a copy to the project folder. Ref-guarded so
      // this + email-send don't both fire in quick succession.
      maybeShowSaveReminder();
    } catch (err) {
      // 409 = possible-duplicate-finalized-meeting. Server returns a
      // ranked list of similar meetings (fuzzy title + same date + same
      // project). Open the merge-or-finalize dialog so the user can:
      //   (a) merge this draft into the existing finalized record
      //   (b) finalize anyway as a separate record
      //   (c) cancel and adjust the title/date
      if (err instanceof ApiError && err.status === 409) {
        const payload = err.data as { duplicates?: DuplicateCandidate[] };
        if (payload.duplicates && payload.duplicates.length > 0) {
          setMergeDialog({ open: true, duplicates: payload.duplicates });
          return;
        }
      }
      throw err;
    }
  }

  // 4i.i + 4i.ii — Delete flow with Studio Modal confirmation and a
  // 30-second undo banner.
  //
  // Flow:
  //   1. User clicks Delete → setConfirmDeleteOpen(true)
  //   2. Modal confirms → softDeleteMutation fires → server sets
  //      status='soft_deleted'
  //   3. undoDeadline is set 30s in the future. A red banner with a
  //      countdown + Undo button overlays the page; the rest of the
  //      editor is non-interactive (opacity-50 + pointer-events-none)
  //      so nothing accidentally mutates a deleted row.
  //   4. Undo → restoreMutation → meeting bounces back to 'draft',
  //      banner clears, editor returns to normal.
  //   5. 30s elapse → navigate to dashboard.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [undoDeadline, setUndoDeadline] = useState<number | null>(null);
  const [undoRemainingMs, setUndoRemainingMs] = useState(0);

  const softDeleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No meeting id');
      await api.post(`/meetings/${id}/soft-delete`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
      setConfirmDeleteOpen(false);
      setUndoDeadline(Date.now() + 30_000);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No meeting id');
      return api.post<Meeting>(`/meetings/${id}/restore`);
    },
    onSuccess: (updated) => {
      qc.setQueryData(['meeting', id], updated);
      qc.invalidateQueries({ queryKey: ['meetings'] });
      setUndoDeadline(null);
    },
  });

  // 4h.i — Unlock a finalized meeting so anyone with read access can
  // edit it again. Owner + admin + firm-wide visibility is preserved
  // (the access model doesn't change) — we just flip status back to
  // 'draft' and clear finalizedAt.
  const unlockMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No meeting id');
      return api.post<Meeting>(`/meetings/${id}/unlock`);
    },
    onSuccess: (updated) => {
      qc.setQueryData(['meeting', id], updated);
    },
  });

  // Countdown tick for the undo banner — re-renders the remaining-ms
  // number 5× / second so the label stays smooth. When the deadline
  // passes, clear state + navigate away.
  useEffect(() => {
    if (undoDeadline === null) return;
    const tick = () => {
      const remaining = undoDeadline - Date.now();
      if (remaining <= 0) {
        setUndoDeadline(null);
        navigate('/');
      } else {
        setUndoRemainingMs(remaining);
      }
    };
    tick();
    const interval = setInterval(tick, 200);
    return () => clearInterval(interval);
  }, [undoDeadline, navigate]);

  const exportMutation = useMutation({
    mutationFn: async (spec: {
      format: 'docx' | 'pdf';
      variant: 'client_meeting' | 'internal';
    }) => {
      if (!id) throw new Error('No meeting id');
      await downloadFile(
        `/meetings/${id}/export/${spec.format}`,
        { variant: spec.variant === 'internal' ? 'internal' : 'client' },
        `${draft?.title || 'meeting-minutes'}.${spec.format}`,
      );
    },
    onSuccess: () => {
      // Offer the follow-up suggestion after a successful export, same
      // as after finalize. For users whose "done" moment is clicking
      // Export rather than Finalize, this is the natural hook point.
      void maybeSuggestFollowup();
    },
  });

  // Editor tour — only fires once structured content exists, so the
  // right-pane preview step actually has something to point at. Hook
  // MUST run before any early returns so React's hook-order invariant
  // holds across loading / loaded states. `enabled` gates the actual
  // work; the hook itself is always called.
  useTour({
    pageKey: 'editor',
    enabled: !!draft?.structuredContent,
    steps: [
      {
        id: 'editor-workpanel',
        title: 'Edit details on the left',
        text: 'Title, date, attendees, action items, and topic bullets live here. Changes auto-save as you type. Drag bullets to reorder, click status icons to mark action items done, and resolve any yellow ambiguity chips Claude flagged.',
        attachTo: '[data-tour="editor-workpanel"]',
        on: 'right',
      },
      {
        id: 'editor-preview',
        title: 'Live document preview',
        text: 'The right side renders the actual letter-size document \u2014 OJB letterhead, page numbers, and proper page breaks included. Click any topic, bullet, or action item to edit in place; left and right always stay in sync.',
        attachTo: '[data-tour="editor-preview"]',
        on: 'left',
      },
      {
        id: 'editor-actions',
        title: 'Export, email, finalize',
        text: 'Download .docx or .pdf, email the draft (opens in Outlook with attachments ready), or finalize to lock the version. Emailing auto-finalizes; finalize also flags potential duplicates and suggests a follow-up meeting if Claude detects one.',
        attachTo: '[data-tour="editor-actions"]',
        on: 'bottom',
      },
    ],
  });

  if (meeting.isLoading || !draft) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-20 text-center text-sm text-smoke">
        Loading…
      </div>
    );
  }
  if (meeting.error) {
    const msg = meeting.error instanceof ApiError ? meeting.error.message : 'Load failed';
    return (
      <div className="mx-auto max-w-5xl px-6 py-20 text-center">
        <div className="rounded border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          {msg}
        </div>
        <Link to="/" className="mt-4 inline-block text-sm text-pine">
          ← Back to dashboard
        </Link>
      </div>
    );
  }
  const m = meeting.data!;
  const structuredContent = draft.structuredContent;

  return (
    // Full-width viewer: the document pane needs 8.5in (816px) plus breathing
    // room, and the form pane is flexible beside it. Use full viewport width
    // on editor routes; the dashboard + other routes still use max-w-6xl.
    <div className="px-6 py-8">
      {/* ─── Hero header ───────────────────────────────────────────────
           Mockup .editor-header: cloud + paper-grain texture, asymmetric
           corners (16/60/40/16) — mirrors the dashboard hero's geometry
           so the editor feels like the same product. Status eyebrow +
           title sit on the left; .icon-btn action cluster on the right. */}
      <div
        className="paper-grain motion-safe:animate-fade-in-up relative mb-3 grid grid-cols-1 gap-6 overflow-hidden rounded-tl-[16px] rounded-tr-[60px] rounded-bl-[40px] rounded-br-[16px] border border-charcoal/10 bg-cloud px-7 py-[22px] shadow-sm md:grid-cols-[1fr_auto] md:items-start"
      >
        <div className="relative min-w-0">
          {/* Status eyebrow — compact, alone on its row. */}
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-pine">
            {m.status === 'finalized' ? 'Finalized' : 'Draft'}
          </div>
          {/* Title — 26px Aeonik 400 per mockup .editor-header h1.
              Lighter weight than the dashboard's H1 since this is a
              utility surface (filling out a document), not a hero. */}
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <h1 className="text-[26px] font-normal leading-[1.2] tracking-tight text-charcoal">
              {draft.title || 'Untitled'}
            </h1>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-charcoal/65">
            <span>
              Version {m.version} · Updated{' '}
              {new Date(m.updatedAt).toLocaleString()}
            </span>
            <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-current opacity-50" />
            <AutoSaveIndicator status={autoSaveStatus} />
          </div>
        </div>
        {/* Action button cluster — uses the .icon-btn family from the
            mockup: bone bg + charcoal/12 border, 13px medium, padding
            8/14px, hover lifts + tints pine + sits on pine-soft bg.
            Three variants: default (Email/.docx/.pdf), --primary
            (Finalize/Unlock — pine fill cream text), --danger
            (Delete — terra-cotta hover tint). */}
        <div
          data-tour="editor-actions"
          className="relative flex flex-wrap items-start justify-end gap-2"
        >
          <button
            type="button"
            onClick={() => setEmailDialogOpen(true)}
            disabled={!structuredContent}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-charcoal/12 bg-bone px-3.5 py-2 text-[13px] font-medium text-charcoal transition-all hover:-translate-y-px hover:border-pine hover:bg-pine/[0.08] hover:text-pine active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:border-charcoal/12 disabled:hover:bg-bone disabled:hover:text-charcoal"
            title={
              structuredContent
                ? 'Review recipients + subject + body, then download an .eml that pops open Outlook Desktop.'
                : 'Transform the meeting with Claude first'
            }
          >
            <Mail size={14} />
            Email
          </button>

          {/* Export: two plain icon-btns, each downloading the
              corresponding format with the current templateVariant
              (variant is a header-level concept, not export-time). */}
          <button
            type="button"
            onClick={() =>
              exportMutation.mutate({
                format: 'docx',
                variant: 'client_meeting',
              })
            }
            disabled={!structuredContent || exportMutation.isPending}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-charcoal/12 bg-bone px-3.5 py-2 text-[13px] font-medium text-charcoal transition-all hover:-translate-y-px hover:border-pine hover:bg-pine/[0.08] hover:text-pine active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:border-charcoal/12 disabled:hover:bg-bone disabled:hover:text-charcoal"
            title="Export Word (.docx) using the current variant"
          >
            {exportMutation.isPending ? 'Exporting…' : '↓ .docx'}
          </button>
          <button
            type="button"
            onClick={() =>
              exportMutation.mutate({
                format: 'pdf',
                variant: 'client_meeting',
              })
            }
            disabled={!structuredContent || exportMutation.isPending}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-charcoal/12 bg-bone px-3.5 py-2 text-[13px] font-medium text-charcoal transition-all hover:-translate-y-px hover:border-pine hover:bg-pine/[0.08] hover:text-pine active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:border-charcoal/12 disabled:hover:bg-bone disabled:hover:text-charcoal"
            title="Export PDF using the current variant"
          >
            {exportMutation.isPending ? 'Exporting…' : '↓ .pdf'}
          </button>

          {m.status === 'draft' && (
            <button
              type="button"
              onClick={() => handleFinalize()}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-pine bg-pine px-4 py-2 text-[13px] font-medium text-cloud transition-all hover:-translate-y-px hover:bg-pine-deep hover:shadow-md active:scale-[0.97]"
            >
              Finalize
            </button>
          )}

          {/* Unlock to edit, shown on finalized meetings. Any OJB user
              who can read this meeting can flip it back to draft. */}
          {m.status === 'finalized' && (
            <button
              type="button"
              onClick={() => unlockMutation.mutate()}
              disabled={unlockMutation.isPending}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-pine bg-pine px-4 py-2 text-[13px] font-medium text-cloud transition-all hover:-translate-y-px hover:bg-pine-deep hover:shadow-md active:scale-[0.97] disabled:opacity-50"
              title="Flip this meeting back to draft so you can edit it. Anyone can do this; the prior owner stays attributed."
            >
              {unlockMutation.isPending ? (
                'Unlocking…'
              ) : (
                <>
                  <Unlock size={14} />
                  Unlock to edit
                </>
              )}
            </button>
          )}

          {/* Delete — icon-btn--danger: same chrome as the default
              icon-btn but text + hover tint shift to terra-cotta. */}
          <button
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-charcoal/12 bg-bone px-3.5 py-2 text-[13px] font-medium text-terra-cotta transition-all hover:-translate-y-px hover:border-terra-cotta hover:bg-terra-cotta/10 active:scale-[0.97]"
          >
            Delete
          </button>
        </div>
      </div>
      {exportMutation.isError && (
        // Terra-cotta tint for blocking errors (matches NewMeeting +
        // ProjectAskPanel error chrome for consistency across the app).
        <div className="mb-4 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-4 py-2.5 text-[13px] text-terra-cotta">
          Export failed:{' '}
          {exportMutation.error instanceof Error
            ? exportMutation.error.message
            : 'Unknown error'}
        </div>
      )}

      {/* Merge-or-finalize modal — shown when finalize returns 409 with
          fuzzy duplicate candidates. User picks Merge, Finalize-separately,
          or Cancel. */}
      <MergeOrFinalizeDialog
        meetingId={id ?? ''}
        duplicates={mergeDialog.duplicates}
        isOpen={mergeDialog.open}
        onClose={() => setMergeDialog({ open: false, duplicates: [] })}
        onMerged={(targetId) => {
          setMergeDialog({ open: false, duplicates: [] });
          qc.invalidateQueries({ queryKey: ['meetings'] });
          // Navigate to the target meeting — the draft is now soft-deleted.
          navigate(`/meetings/${targetId}/edit`);
        }}
        onFinalized={() => {
          setMergeDialog({ open: false, duplicates: [] });
          qc.invalidateQueries({ queryKey: ['meeting', id] });
          qc.invalidateQueries({ queryKey: ['meetings'] });
          // Same follow-up detection trigger as the regular finalize
          // path — the user just confirmed they want to finalize despite
          // the duplicate warning, so treat it as a finalize completion.
          void maybeSuggestFollowup();
          maybeShowSaveReminder();
        }}
      />

      {/* Email-send modal — mounted here so the editor's Email button
          controls it. Defaults are computed once from the current draft;
          the dialog resets its internal state on each open.
          Attendee emails captured at meeting creation from the Outlook
          event prefill the To (non-OJB) + CC (@ojb.com) fields. */}
      <SendEmailDialog
        meetingId={id ?? ''}
        meetingTitle={draft.title || 'Untitled'}
        defaultSubject={`Meeting Minutes - ${draft.title || 'OJB'}`}
        defaultBody=""
        defaultVariant="client_meeting"
        defaultTo={(m.attendeeEmails ?? [])
          .filter((a) => !a.isOjb && a.email && a.email.trim().length > 0)
          .map((a) => a.email)
          .join(', ')}
        defaultCc={(m.attendeeEmails ?? [])
          .filter((a) => a.isOjb && a.email && a.email.trim().length > 0)
          .map((a) => a.email)
          .join(', ')}
        isOpen={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        onSent={() => {
          // Server auto-finalizes on successful send. Refresh meeting
          // data so the editor reflects the new status.
          qc.invalidateQueries({ queryKey: ['meeting', id] });
          qc.invalidateQueries({ queryKey: ['meetings'] });
          // And trigger follow-up detection — downloading the .eml is
          // the most common "I'm done" signal in practice.
          void maybeSuggestFollowup();
          maybeShowSaveReminder();
        }}
      />

      {/* Follow-up invite flow — appears after finalize/email/export when
          Claude detects a follow-up was discussed. Specific-date hits
          skip the prompt and jump straight to the form; vague hits show
          the yes/no prompt first so the user can dismiss in one click. */}
      <FollowupPromptDialog
        isOpen={
          followupDialog.mode === 'prompt' &&
          followupDialog.detection !== null
        }
        evidenceQuote={followupDialog.detection?.evidenceQuote}
        onDismiss={() =>
          setFollowupDialog({ mode: 'closed', detection: null })
        }
        onConfirm={() =>
          // Transition from yes/no prompt → full invite form. Keep
          // detection so the evidence quote + proposed title survive
          // into the form (the date stays empty since it's a vague
          // detection).
          setFollowupDialog((prev) =>
            prev.detection
              ? { mode: 'form', detection: prev.detection }
              : prev,
          )
        }
      />
      <FollowupSuggestionDialog
        isOpen={
          followupDialog.mode === 'form' &&
          followupDialog.detection !== null
        }
        detection={followupDialog.detection}
        sourceMeetingId={id ?? ''}
        sourceMeetingTitle={draft.title || 'Meeting'}
        availableAttendees={(m.attendeeEmails ?? [])
          // Follow-up invite needs email to add a recipient — externals
          // added by name only can't be invited via Graph, so filter
          // them out of the chip list.
          .filter(
            (a): a is { name: string; email: string; isOjb: boolean } =>
              !!a.email && a.email.trim().length > 0,
          )
          .map((a) => ({
            name: a.name,
            email: a.email,
            isOjb: a.isOjb,
          }))}
        onClose={() =>
          setFollowupDialog({ mode: 'closed', detection: null })
        }
        onCreated={() => {
          // Small delay so the user sees the success panel before the
          // dialog closes. They close it explicitly via the Done button.
        }}
      />

      {/* Soft "save to the project management folder" reminder. Toast,
          bottom-right, auto-dismisses. Quick .docx / .pdf download
          buttons reuse the main exportMutation so the files land with
          the same brand-compliant output the user would get from the
          Export button in the header. */}
      <SaveToFolderToast
        isOpen={saveReminderOpen}
        isDownloading={exportMutation.isPending}
        onDownloadDocx={() =>
          exportMutation.mutate({
            format: 'docx',
            variant: 'client_meeting',
          })
        }
        onDownloadPdf={() =>
          exportMutation.mutate({
            format: 'pdf',
            variant: 'client_meeting',
          })
        }
        onDismiss={() => setSaveReminderOpen(false)}
      />

      {/* Re-transform confirmation (4d.ii) — only opens when the user
          clicks Transform on a meeting that already has structured
          content. First transform fires directly without the dialog. */}
      <Modal
        isOpen={confirmRetransformOpen}
        onClose={() => setConfirmRetransformOpen(false)}
        size="md"
        ariaLabel="Confirm re-transform"
      >
        <ModalHeader
          title="Re-transform this meeting?"
          subtitle="Claude will replace the current structured content with a fresh interpretation of your raw input. Any inline edits you made to the document will be lost."
          onClose={() => setConfirmRetransformOpen(false)}
        />
        <ModalBody>
          <p className="text-sm text-charcoal/75">
            You normally re-transform when you&rsquo;ve updated the raw
            input meaningfully (e.g. pasted a new transcript or fixed
            shorthand). If you just want to tweak wording in the
            document, edit the minutes directly in the right pane
            instead - those changes autosave.
          </p>
        </ModalBody>
        <ModalFooter className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmRetransformOpen(false)}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirmRetransformOpen(false);
              transformMutation.mutate();
            }}
            className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90"
          >
            Re-transform
          </button>
        </ModalFooter>
      </Modal>

      {/* Auto-suggested title banner (4e.iv) — appears after the first
          transform when the meeting's title is blank or a generic
          placeholder. Clicking Accept applies the title; Dismiss
          closes the banner and leaves the title alone. */}
      {suggestedTitle && (
        // Mockup .banner--suggest: pine-soft bg, pine/25 border,
        // rounded-[14px], padding 12/18px. Adds a small "SUGGESTION"
        // eyebrow above the title text so the banner reads as a
        // labeled affordance, not just a flash of pine.
        <div className="mb-3 flex items-start justify-between gap-4 rounded-[14px] border border-pine/25 bg-pine/[0.06] px-[18px] py-3 text-[13px] text-pine">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-pine/85">
              Suggestion
            </div>
            <div className="mt-1">
              <span className="font-semibold">Suggested title:</span>{' '}
              <span className="italic">&ldquo;{suggestedTitle}&rdquo;</span>
            </div>
            <div className="mt-1 text-[12px] text-pine/75">
              Claude proposed this from the transformed content. Accept
              to apply, or dismiss to keep the current title.
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => {
                updateTitle(suggestedTitle);
                setSuggestedTitle(null);
              }}
              className="rounded-full bg-pine px-3.5 py-1.5 text-[12px] font-medium text-cloud transition-colors hover:bg-pine-deep"
            >
              Accept
            </button>
            <button
              type="button"
              onClick={() => setSuggestedTitle(null)}
              aria-label="Dismiss"
              className="rounded-full p-1.5 text-base text-pine/60 transition-colors hover:bg-pine/10 hover:text-pine"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation Modal (4i.i) */}
      <Modal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        size="md"
        ariaLabel="Confirm delete"
      >
        <ModalHeader
          title="Delete this meeting?"
          subtitle="Moves the meeting to the trash. You'll have 30 seconds to undo before it's gone from your dashboard."
          onClose={() => setConfirmDeleteOpen(false)}
        />
        <ModalBody>
          <p className="text-sm text-charcoal/80">
            The meeting + all its structured content stay recoverable
            from the admin portal afterwards. Use Undo during the
            30-second window if you change your mind.
          </p>
          {softDeleteMutation.isError && (
            <div className="mt-3 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-3 py-2 text-[12px] text-terra-cotta">
              {softDeleteMutation.error instanceof Error
                ? softDeleteMutation.error.message
                : 'Delete failed'}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={softDeleteMutation.isPending}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
          >
            Cancel
          </button>
          {/* Terra-cotta destructive button — on-brand for "delete"
              actions. Mockup uses terra-cotta for any irreversible
              action affordance; reserved red for hard system errors. */}
          <button
            type="button"
            onClick={() => softDeleteMutation.mutate()}
            disabled={softDeleteMutation.isPending}
            className="rounded-full bg-terra-cotta px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-terra-cotta/90 disabled:opacity-50"
          >
            {softDeleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </ModalFooter>
      </Modal>

      {/* Undo banner (4i.ii) — appears for 30s after soft-delete.
          Fixed-position at the bottom so it's visible regardless of
          scroll and doesn't push editor content around. Button triggers
          restoreMutation; timer drives setUndoDeadline → navigate. */}
      {undoDeadline !== null && (
        // Bottom-fixed charcoal toast — kept the existing
        // dark-on-light treatment because the undo affordance must
        // pop against ANY page background. 13px medium label, pine
        // pill for the timed action button.
        <div
          className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 rounded-full border border-charcoal/20 bg-charcoal px-5 py-3 text-[13px] text-cloud shadow-[0_12px_28px_rgba(0,0,0,0.35)]"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-4">
            <span className="font-medium">Meeting deleted.</span>
            <button
              type="button"
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
              className="rounded-full bg-pine px-3.5 py-1 text-[12px] font-medium text-cloud transition-colors hover:bg-pine-deep disabled:opacity-50"
            >
              {restoreMutation.isPending
                ? 'Restoring…'
                : `Undo (${Math.ceil(undoRemainingMs / 1000)}s)`}
            </button>
          </div>
        </div>
      )}

      {/* Concurrency conflict banner — shows when useAutoSave's PATCH
          returned 409 because the row's version was bumped elsewhere
          (another tab, another user, or a server-side mutation like
          a transform from a different session). We surface a manual
          reload button instead of auto-reloading so the user doesn't
          lose any in-progress edits they haven't saved yet. */}
      {autoSaveStatus.kind === 'conflict' && (
        // Mockup .banner pattern with terra-cotta accent — destructive
        // / blocking message tone. Adds an eyebrow label so the banner
        // category is scannable at a glance.
        <div className="mb-3 flex items-start justify-between gap-4 rounded-[14px] border border-terra-cotta/40 bg-terra-cotta/[0.08] px-[18px] py-3 text-[13px] text-terra-cotta">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-terra-cotta/85">
              Edit conflict
            </div>
            <div className="mt-1">
              <span className="font-semibold">
                This meeting was edited in another tab or window.
              </span>{' '}
              Your next auto-save will keep failing until you reload.
              Reloading pulls the latest server version - any unsaved
              local edits to this meeting will be lost.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (id) {
                void qc.invalidateQueries({ queryKey: ['meeting', id] });
              }
            }}
            className="shrink-0 rounded-full bg-terra-cotta px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-terra-cotta/90"
          >
            Reload
          </button>
        </div>
      )}

      {/* Two-pane workspace — width split is per-user persisted via
          /me.editor_split_percent (default 50). EditorSplitPane wraps
          the existing left/right children and adds a draggable
          vertical handle, double-click-to-collapse, and keyboard
          arrow nudges. Below md, panes stack vertically (handle
          inert) — a sub-400px pane on a phone would be unreadable
          regardless of split. */}
      <EditorSplitPane
        ref={splitRef}
        initialPercent={splitPercent}
        onCommit={persistSplitPercent}
        left={
        <div
          data-tour="editor-workpanel"
          className="motion-safe:animate-fade-in-up h-full space-y-3.5 px-6 py-2"
          style={{ animationDelay: '80ms' }}
        >
          {/* Mockup .panel: bone bg + charcoal/8 + rounded-2xl(16px) +
              padding 20/22px + mb-3.5. The panel head is bordered-
              bottom (charcoal/8) so the title row reads as a header
              for the form below it, not just a label. */}
          <section className="rounded-2xl border border-charcoal/8 bg-bone px-[22px] py-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-charcoal/8 pb-3">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.22em] text-pine">
                Meeting details
              </h2>
              <div className="flex items-center gap-2">
                {/* Restore last saved — pulls the server's current
                    state back into the draft, undoing any in-flight
                    typed-but-not-yet-saved changes (window of up to
                    DEBOUNCE_MS in useAutoSave, currently 1.5s).
                    Resets meeting metadata + structuredContent
                    wholesale, including attendeeGroups (firms /
                    members live in structuredContent). Tiptap's
                    own undo stack on the right pane is independent
                    of this. */}
                <button
                  type="button"
                  onClick={() => {
                    if (!meeting.data) return;
                    if (
                      !window.confirm(
                        'Discard unsaved changes and restore the last saved version? This affects fields in the left pane and the attendees panel.',
                      )
                    ) {
                      return;
                    }
                    setDraft({
                      title: meeting.data.title,
                      location: meeting.data.location ?? '',
                      meetingDate: meeting.data.meetingDate,
                      notesDate: meeting.data.notesDate,
                      meetingFormat: meeting.data.meetingFormat,
                      projectAlias: meeting.data.projectAlias ?? '',
                      rawInput: meeting.data.rawInput,
                      templateVariant:
                        meeting.data.templateVariant === 'internal'
                          ? 'internal'
                          : 'client_meeting',
                      structuredContent: isStructuredContent(
                        meeting.data.structuredContent,
                      )
                        ? (meeting.data.structuredContent as StructuredContent)
                        : null,
                    });
                  }}
                  disabled={m.status !== 'draft'}
                  title="Discard unsaved changes and restore the last saved version (covers left-pane fields + attendees). Right-pane preview has its own undo via Cmd/Ctrl+Z while focused there."
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-charcoal/12 bg-cream px-3 py-1 text-[11px] font-medium text-charcoal/70 transition-all hover:border-pine hover:bg-pine/[0.08] hover:text-pine disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-charcoal/12 disabled:hover:bg-cream disabled:hover:text-charcoal/70"
                >
                  ↺ Restore last saved
                </button>
                <PageNumbersToggle
                  enabled={showPageNumbers}
                  onToggle={togglePageNumbers}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <label className="block">
                <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-pine/85">
                  Title
                </span>
                <input
                  {...NO_AUTOFILL}
                  type="text"
                  value={draft.title}
                  onChange={(e) => updateTitle(e.target.value)}
                  className="w-full rounded-[10px] border border-charcoal/10 bg-cream px-3.5 py-2.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-pine/85">
                    Meeting date
                  </span>
                  <input
                    {...NO_AUTOFILL}
                    type="date"
                    value={draft.meetingDate}
                    onChange={(e) => updateMeetingDate(e.target.value)}
                    className="w-full rounded-[10px] border border-charcoal/10 bg-cream px-3.5 py-2.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-pine/85">
                    Notes date
                  </span>
                  <input
                    {...NO_AUTOFILL}
                    type="date"
                    value={draft.notesDate}
                    onChange={(e) => updateNotesDate(e.target.value)}
                    className="w-full rounded-[10px] border border-charcoal/10 bg-cream px-3.5 py-2.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-pine/85">
                    Location
                  </span>
                  <input
                    {...NO_AUTOFILL}
                    type="text"
                    name="meeting-venue"
                    value={draft.location}
                    onChange={(e) => updateLocation(e.target.value)}
                    className="w-full rounded-[10px] border border-charcoal/10 bg-cream px-3.5 py-2.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-pine/85">
                    Meeting type
                  </span>
                  <select
                    value={draft.meetingFormat ?? ''}
                    onChange={(e) =>
                      updateMeetingFormat(
                        (e.target.value || null) as
                          | 'in_person'
                          | 'video'
                          | 'phone'
                          | 'hybrid'
                          | null,
                      )
                    }
                    className="w-full rounded-[10px] border border-charcoal/10 bg-cream px-3.5 py-2.5 text-[14px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                  >
                    <option value="">-</option>
                    <option value="video">Video (Teams/Zoom)</option>
                    <option value="in_person">In person</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="phone">Phone</option>
                  </select>
                </label>
              </div>
              {/* Project alias — display-only override for the project
                  name shown in the rendered minutes. The autosave hook
                  PATCHes this on debounce; the server mirrors the new
                  value into structuredContent.project, then the
                  autoSaveStatus.onSaved callback (in this file, just
                  above) propagates that back into draft.structuredContent
                  so the right-pane preview updates.
                  We INTENTIONALLY don't eager-mirror here on every
                  keystroke. The earlier eager-mirror caused the Tiptap
                  editor to re-seed its entire ProseMirror doc on every
                  character (because TiptapStructuredEditor's external-
                  sync useEffect watches draft.structuredContent) — and
                  the rapid setContent calls collided with React's DOM
                  reconciliation, producing a "removeChild" crash in
                  the editor. Worth the ~1.5s delay before the preview
                  updates to keep the editor stable. */}
              <label className="block">
                <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-pine/85">
                  Project alias{' '}
                  <span className="ml-1 text-[9px] font-medium normal-case italic tracking-[0.05em] text-charcoal/55">
                    (display only)
                  </span>
                </span>
                <input
                  {...NO_AUTOFILL}
                  type="text"
                  value={draft.projectAlias}
                  onChange={(e) =>
                    setDraft((d) =>
                      !d ? d : { ...d, projectAlias: e.target.value },
                    )
                  }
                  placeholder="Leave blank to show the real project name"
                  className="w-full rounded-[10px] border border-charcoal/10 bg-cream px-3.5 py-2.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                />
              </label>
            </div>
          </section>

          {/* Attendees — lives here above Raw Input so edits don't
              require scrolling into the paper preview. Bidirectional:
              the preview's AttendeesBlock still edits the same state. */}
          {structuredContent && (
            <AttendeesPanel
              attendeeGroups={structuredContent.attendeeGroups ?? []}
              onChange={(groups) => {
                setDraft((d) =>
                  !d
                    ? d
                    : {
                        ...d,
                        structuredContent: d.structuredContent
                          ? {
                              ...d.structuredContent,
                              attendeeGroups: groups,
                            }
                          : d.structuredContent,
                      },
                );
              }}
              readOnly={m.status !== 'draft'}
            />
          )}

          {/* Raw input panel — same .panel chrome family as the
              Meeting details panel above. Header includes the
              chevron toggle, "Raw input" title, optional token-count
              pill (visible only when collapsed with content), and a
              pine "Transform with Claude" pill on the right. */}
          <section className="rounded-2xl border border-charcoal/8 bg-bone px-[22px] py-5 shadow-sm">
            {/* Header row is always visible. Body collapses once the
                meeting has structured content — the note-taker
                doesn't usually re-read their raw paste after the
                transform, and an expanded textarea takes up real
                estate the document preview could use. */}
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setRawInputOpen((v) => !(v ?? !draft.structuredContent))}
                className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-pine transition-colors hover:text-pine/80"
                aria-expanded={effectiveRawInputOpen}
                aria-controls="raw-input-body"
              >
                <span
                  aria-hidden
                  className="text-pine transition-transform duration-200"
                  style={{
                    transform: effectiveRawInputOpen
                      ? 'rotate(0deg)'
                      : 'rotate(-90deg)',
                  }}
                >
                  <ChevronDown size={14} />
                </span>
                Raw input
                {!effectiveRawInputOpen && draft.rawInput.trim() && (
                  // Token-count pill — pine-soft on pine, 10px medium
                  // tracking. Visible only when collapsed with content
                  // so the user can see how big the paste was without
                  // expanding.
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-pine/[0.08] px-2.5 py-0.5 text-[10px] font-medium tracking-[0.05em] text-pine">
                    {rawInputTokens.toLocaleString()} tok
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={handleTransformClick}
                disabled={overLimit || transformMutation.isPending || !draft.rawInput.trim()}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-pine px-3.5 py-1.5 text-[12px] font-medium text-cloud transition-all hover:-translate-y-px hover:bg-pine-deep disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-pine"
                title={
                  draft.structuredContent
                    ? 'Re-transform - will prompt before overwriting the current structured content.'
                    : undefined
                }
              >
                {transformMutation.isPending ? (
                  'Transforming…'
                ) : (
                  <>
                    <Sparkles size={13} />
                    {draft.structuredContent
                      ? 'Re-transform'
                      : 'Transform with Claude'}
                  </>
                )}
              </button>
            </div>
            {effectiveRawInputOpen && (
              <div id="raw-input-body" className="relative mt-3">
                <textarea
                  {...NO_AUTOFILL}
                  value={draft.rawInput}
                  onChange={(e) => {
                    setDraft({ ...draft, rawInput: e.target.value });
                    // Typing dismisses the paste offer — if you're
                    // editing more, you don't need the prompt.
                    if (showPasteOffer) dismissPasteOffer();
                  }}
                  onPaste={onRawInputPaste}
                  placeholder="Paste transcript, AI notes, or freeform shorthand…"
                  rows={14}
                  // Mockup .raw-input__textarea: cream + charcoal/10 +
                  // rounded-[10px] + 13px Aeonik line-1.55 + min-h-220.
                  // Same writing surface as the new-meeting source
                  // textareas so the pattern reads consistently.
                  className="min-h-[220px] w-full resize-y rounded-[10px] border border-charcoal/10 bg-cream px-3.5 py-3 text-[13px] leading-[1.55] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                />
                {/* Smart-paste prompt — small pine pill that floats
                    in the top-right of the textarea right after a
                    paste. Click to fire transform, X to dismiss. */}
                {showPasteOffer &&
                  !transformMutation.isPending &&
                  draft.rawInput.trim() && (
                    // Mockup .smart-paste: small pine pill floating in
                    // the top-right of the textarea, fired right after
                    // a paste. 11px medium, padding 4/10px, soft pine
                    // shadow.
                    <div className="motion-safe:animate-fade-in-up pointer-events-none absolute right-3 top-3 flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          dismissPasteOffer();
                          handleTransformClick();
                        }}
                        disabled={overLimit}
                        className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-pine px-2.5 py-1 text-[11px] font-medium text-cloud shadow-[0_2px_6px_rgba(55,85,66,0.25)] transition-all hover:-translate-y-px hover:bg-pine-deep disabled:opacity-50"
                        title="Send the pasted content to Claude for structuring"
                      >
                        <Sparkles size={12} />
                        Transform?
                      </button>
                      <button
                        type="button"
                        onClick={dismissPasteOffer}
                        className="pointer-events-auto rounded-full border border-charcoal/15 bg-cream p-1 text-charcoal/55 shadow-sm transition-colors hover:border-charcoal/30 hover:text-charcoal"
                        title="Dismiss"
                        aria-label="Dismiss paste prompt"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  )}
                <div className="mt-3">
                  <TokenMeter tokens={rawInputTokens} />
                </div>

                {/* Append supplemental input (post-draft only).
                    Hidden on first transform — the user already
                    has a full editable textarea and doesn't need
                    a second one. Once a draft exists, this lets
                    them paste a follow-up note / addendum / chat
                    thread without overwriting the original raw
                    input or hand-typing a separator. */}
                {draft.structuredContent && (
                  <div className="mt-4 border-t border-charcoal/10 pt-3">
                    {!supplementalOpen ? (
                      <button
                        type="button"
                        onClick={() => setSupplementalOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-pine/30 bg-pine/[0.06] px-3 py-1 text-[11px] font-medium text-pine transition-colors hover:bg-pine/[0.1]"
                        title="Add a second block of raw input - appends with a dated separator"
                      >
                        <span aria-hidden>＋</span>
                        Append more input
                      </button>
                    ) : (
                      <div className="space-y-2 rounded-[10px] border border-pine/25 bg-pine/[0.04] p-3.5">
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-pine">
                            Append additional input
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSupplementalOpen(false);
                              setSupplementalDraft('');
                              setSupplementalLabel('');
                            }}
                            className="text-charcoal/50 transition-colors hover:text-charcoal"
                            aria-label="Cancel appending input"
                          >
                            <X size={12} />
                          </button>
                        </div>
                        <input
                          {...NO_AUTOFILL}
                          type="text"
                          value={supplementalLabel}
                          onChange={(e) => setSupplementalLabel(e.target.value)}
                          placeholder="Label (optional, e.g. Follow-up email, Pre-meeting agenda)"
                          className="w-full rounded-[8px] border border-charcoal/10 bg-cream px-2.5 py-1.5 text-[12px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                        />
                        <textarea
                          {...NO_AUTOFILL}
                          value={supplementalDraft}
                          onChange={(e) => setSupplementalDraft(e.target.value)}
                          placeholder="Paste the additional content here…"
                          rows={6}
                          className="w-full resize-y rounded-[8px] border border-charcoal/10 bg-cream px-2.5 py-2 text-[13px] leading-relaxed text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                        />
                        <div className="flex items-center justify-between text-[10px] text-charcoal/60">
                          <span>
                            Will be appended below the existing raw input with
                            a dated section header. Re-transform after to
                            merge it into the document.
                          </span>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSupplementalOpen(false);
                              setSupplementalDraft('');
                              setSupplementalLabel('');
                            }}
                            className="rounded-full px-3 py-1 text-[11px] text-charcoal/65 transition-colors hover:bg-charcoal/5 hover:text-charcoal"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={appendSupplementalInput}
                            disabled={!supplementalDraft.trim()}
                            className="rounded-full bg-pine px-3 py-1 text-[11px] font-medium text-cloud transition-colors hover:bg-pine-deep disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Append to raw input
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Streaming progress bar (4d.iii) — visible while the
                transform mutation is pending regardless of collapse
                state, so the user always sees progress after clicking
                Transform. */}
            {transformMutation.isPending && (
              <div className="mt-4">
                <TransformProgressBar
                  phase={transformPhase}
                  progress={transformProgress}
                />
              </div>
            )}
            {transformMutation.isError && (
              <div className="mt-3 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-3.5 py-2 text-[12px] text-terra-cotta">
                {transformMutation.error instanceof Error
                  ? transformMutation.error.message
                  : 'Transform failed'}
              </div>
            )}
            {transformMutation.isSuccess && transformMutation.data?.result && (() => {
              // The mutation function returns { result: TransformResult | null }
              // so the actual transform payload (usage / glossaryMatches /
              // priorMeetingsUsed) lives on data.result, NOT data directly.
              // Pre-existing bug — the previous code read data.usage which
              // was always undefined; it didn't crash earlier only because
              // the success state rarely held long enough to render this
              // block before the next state transition. Once the route-
              // level error boundary started catching the throw, the bug
              // surfaced as "Cannot read properties of undefined (reading
              // 'durationMs')". Hoisting `result` into a local + null-safe
              // chains everywhere fixes it.
              const result = transformMutation.data.result;
              const usage = result.usage;
              return (
                <div className="mt-3 rounded-2xl border border-pine/25 bg-pine/[0.06] px-3.5 py-2 text-[12px] text-pine">
                  {usage && (
                    <div>
                      Transformed in {(usage.durationMs / 1000).toFixed(1)}s
                      {' · '}
                      {usage.inputTokens.toLocaleString()} in /{' '}
                      {usage.outputTokens.toLocaleString()} out tokens
                      {' · '}
                      ${usage.costUsd.toFixed(4)}
                    </div>
                  )}
                  {/* Glossary expansions surfaced from raw-input matches. */}
                  {result.glossaryMatches && result.glossaryMatches.length > 0 && (
                    <div
                      className="mt-1.5 text-[11px] text-pine/80"
                      title="These acronyms from the firm glossary appeared in your raw input and were passed to Claude with instructions to expand them on first use. Hover each to see the expansion."
                    >
                      <span className="font-semibold">Glossary expansions:</span>{' '}
                      {result.glossaryMatches.map((g, i) => (
                        <span key={g.term}>
                          {i > 0 && ', '}
                          <span title={g.expansion} className="cursor-help">
                            {g.term}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Prior finalized meetings from the same project that were
                      injected as project memory. */}
                  {result.priorMeetingsUsed && result.priorMeetingsUsed.length > 0 && (
                    <div
                      className="mt-1.5 text-[11px] text-pine/80"
                      title="Claude saw these prior finalized meetings for this project as context. It used them to match established vocabulary, recognize recurring attendees, and maintain continuity - it was NOT told to copy content forward."
                    >
                      <span className="font-semibold">
                        Drew on {result.priorMeetingsUsed.length}{' '}
                        prior meeting
                        {result.priorMeetingsUsed.length === 1 ? '' : 's'}:
                      </span>{' '}
                      {result.priorMeetingsUsed.map((p, i) => (
                        <span key={p.id}>
                          {i > 0 && ', '}
                          <a
                            href={`/meetings/${p.id}/edit`}
                            className="underline-offset-2 hover:underline"
                            title={`${p.title} - ${p.meetingDate}`}
                          >
                            {p.meetingDate}
                          </a>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </section>

          {/* AI enhancements: polish, translate, suggest title */}
          {m.status === 'draft' && (
            <AiEnhancements
              meeting={m as Parameters<typeof AiEnhancements>[0]['meeting']}
              hasStructuredContent={
                !!structuredContent && (structuredContent.topics?.length ?? 0) > 0
              }
              onMeetingUpdated={(next) => {
                qc.setQueryData(['meeting', id], next);
                // When Claude (polish/translate) or a server revert updates
                // structured_content, ALSO re-sync the form mirrors so the
                // title input, location, and date fields reflect the new
                // language / content. Previously only structuredContent was
                // synced, which left a stale (translated) title stuck in
                // the form after a revert.
                const sc = isStructuredContent(next.structuredContent)
                  ? (next.structuredContent as StructuredContent)
                  : null;
                setDraft((d) =>
                  !d
                    ? d
                    : {
                        ...d,
                        structuredContent: sc ?? d.structuredContent,
                        title: next.title ?? d.title,
                        location: next.location ?? '',
                        meetingDate: next.meetingDate ?? d.meetingDate,
                        notesDate: next.notesDate ?? d.notesDate,
                        meetingFormat: next.meetingFormat ?? d.meetingFormat,
                      },
                );
              }}
              onTitleSuggested={(title) => {
                updateTitle(title);
              }}
            />
          )}
        </div>
        }
        right={
        /* Right: paper document on a desk-brown background.
           ScaledPaper wraps the content in a CSS zoom container that
           resizes the letter-sheet to fit the available pane width
           (clamped 0.5x .. 1.6x), so the document reads at the same
           apparent size on laptop, regular, widescreen, and ultra-
           wide monitors alike. */
        <ScaledPaper
          ref={paperContainerRef}
          data-tour="editor-preview"
          className="motion-safe:animate-fade-in-up h-full bg-cloud/50"
          style={{ animationDelay: '160ms' }}
          topLeftBadge={
            <DocumentStatsBadge
              content={structuredContent}
              pageCount={useTiptap ? tiptapPageCount : undefined}
            />
          }
          onPaperWidthChange={(px) =>
            splitRef.current?.accommodatePaperWidth(px)
          }
        >
          {transformMutation.isPending && !structuredContent ? (
            // No prior content + transform in flight — show a paper-
            // shaped skeleton so the layout doesn't jump and the user
            // sees something happening even before the first stream
            // chunk arrives.
            <PaperSkeleton />
          ) : structuredContent && structuredContent.topics?.length ? (
            useTiptap ? (
              // Tiptap rebuild — single contenteditable surface
              // for Word-doc-feel editing. Phase 3 wires
              // bidirectional sync: in-editor edits persist via
              // onChange (mirror logic matches the legacy editor
              // below so autosave + form mirrors stay consistent),
              // and form-pane edits re-seed the editor on the next
              // render.
              //
              // Wrapped in StructuredEditorErrorBoundary so a
              // render-time crash inside Tiptap (most often
              // during re-seed after a re-transform when the new
              // structured_content fights the schema) doesn't
              // unmount the entire route and leave the user with
              // a blank page. They get a "Retry editor" card
              // instead, with raw input + saved content intact.
              <StructuredEditorErrorBoundary>
                <TiptapStructuredEditor
                  // Forced re-mount on readOnly transitions. Without
                  // this key, toggling editable at runtime collides
                  // with React's reconciliation (every NodeView
                  // re-renders with a new isEditable, Tiptap mutates
                  // DOM mid-commit) and crashes with "removeChild on
                  // Node — not a child of this node". The crash hit
                  // every Finalize click. Re-mounting via key drops
                  // cursor position + undo history, which is fine
                  // on a draft↔finalized transition since the user
                  // is changing editing modes anyway. Tiptap mounts
                  // fresh with the correct initial `editable` value
                  // baked in at construction.
                  key={m.status === 'draft' ? 'draft' : 'readonly'}
                  content={structuredContent}
                  onChange={(next) =>
                    setDraft({
                      ...draft,
                      structuredContent: next,
                      title: next.title,
                      location: next.location ?? '',
                      meetingDate: next.meetingDate,
                      notesDate: next.notesDate,
                    })
                  }
                  readOnly={m.status !== 'draft'}
                  showPageNumbers={showPageNumbers}
                  meetingId={m.id}
                  onPageCountChange={setTiptapPageCount}
                  // Live-mirror the project alias into the right-pane
                  // preview without touching draft.structuredContent.
                  // The editor's own useEffect debounces this and
                  // applies a single-attr PM transaction on the
                  // meetingHeader node — surgical, doesn't trigger
                  // the full doc re-seed that previously caused the
                  // "removeChild on Node" crash. When alias is empty
                  // we don't pass an override, so the preview keeps
                  // showing whatever's already in the doc (the real
                  // project name from the last save).
                  projectOverride={
                    draft.projectAlias.trim().length > 0
                      ? draft.projectAlias.trim()
                      : undefined
                  }
                />
              </StructuredEditorErrorBoundary>
            ) : (
              <EditableStructuredPreview
                content={structuredContent}
                onChange={(next) =>
                  setDraft({
                    ...draft,
                    structuredContent: next,
                    // Keep the form mirrors in lockstep so the header + form input
                    // reflect edits made directly in the document preview. These
                    // also get picked up by autosave's PATCH payload so the
                    // meetings row columns stay consistent with structured_content.
                    title: next.title,
                    location: next.location ?? '',
                    meetingDate: next.meetingDate,
                    notesDate: next.notesDate,
                  })
                }
                readOnly={m.status !== 'draft'}
                showPageNumbers={showPageNumbers}
              />
            )
          ) : (
            <div
              className="mx-auto bg-white text-center shadow-[0_2px_16px_rgba(0,0,0,0.12)] ring-1 ring-black/5"
              style={{
                width: '8.5in',
                minHeight: '11in',
                paddingTop: '3in',
                paddingLeft: '2.85in',
                paddingRight: '0.7in',
              }}
            >
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-pine">
                No structured content yet
              </div>
              <div className="mt-3 text-sm text-smoke">
                Click <b>Transform with Claude</b> on the left to generate minutes.
              </div>
            </div>
          )}
        </ScaledPaper>
        }
      />

      <div className="mt-8">
        <Link to="/" className="text-sm text-pine">
          ← Back to dashboard
        </Link>
      </div>

      {/* Floating rewrite bubble — appears on text selection inside the paper
          pane. Hidden for finalized meetings (no-edit mode), AND hidden when
          the Tiptap editor is active because that editor ships its own
          TiptapBubbleMenu (with the same Shorter/More formal/Expand/Simpler
          presets plus the new B/I/U + Speaker/Quote affordances). Showing both
          stacked the legacy strip below the new pine-pill toolbar — see
          pilot screenshot Apr 25 2026. */}
      {m.status === 'draft' && structuredContent && !useTiptap && (
        <RewriteBubble
          meetingId={m.id}
          containerRef={paperContainerRef}
          onApply={({ selectedText, rewrittenText }) => {
            setDraft({
              ...draft,
              structuredContent: replaceFirstSnippet(
                structuredContent,
                selectedText,
                rewrittenText,
              ),
            });
          }}
        />
      )}
    </div>
  );
}

/**
 * Autosave indicator — a colored dot + label. Colors encode state at
 * a glance: charcoal=idle, pine=saved, amber=saving, red=error or
 * conflict. The dot pulses on 'saving' to differentiate from the
 * static-dot saved state.
 */
function AutoSaveIndicator({ status }: { status: ReturnType<typeof useAutoSave> }) {
  // Mockup .save-indicator: 11px pine medium, gap-1.5, with a 6px
  // pine dot that emits a continuous expanding-ring pulse via the
  // ::after pseudo. Different states swap the dot color + label
  // text + drop the pulse ring for non-active states.
  let dotClass = 'bg-charcoal/40';
  let label = 'Saved';
  let labelClass = 'text-charcoal/60';
  let title = 'All changes persisted.';
  // ringPulse → expanding-ring animation around the dot. 'saved'
  // gets a slow ambient ring (the "we just saved" beat); 'saving'
  // gets a fast amber dot pulse (the work-in-progress beat).
  let ringPulse: 'pine' | null = null;
  let dotPulse = false;

  switch (status.kind) {
    case 'idle':
      dotClass = 'bg-charcoal/30';
      label = 'Saved';
      labelClass = 'text-charcoal/55';
      title = 'No changes pending.';
      break;
    case 'saving':
      dotClass = 'bg-amber-500';
      label = 'Saving…';
      labelClass = 'text-charcoal/70';
      title = 'Writing changes to the server.';
      dotPulse = true;
      break;
    case 'saved':
      dotClass = 'bg-pine';
      label = `Saved ${status.at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
      labelClass = 'text-pine font-medium';
      title = 'All changes persisted.';
      ringPulse = 'pine';
      break;
    case 'conflict':
      dotClass = 'bg-terra-cotta';
      label = 'Edit conflict - reload';
      labelClass = 'text-terra-cotta font-medium';
      title = 'This meeting was updated in another tab. Reload to sync.';
      break;
    case 'error':
      dotClass = 'bg-terra-cotta';
      label = `Save failed: ${status.message}`;
      labelClass = 'text-terra-cotta';
      title = status.message;
      break;
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px]"
      title={title}
      aria-live="polite"
    >
      <span
        aria-hidden
        className={
          'relative inline-block h-1.5 w-1.5 rounded-full ' +
          dotClass +
          (dotPulse ? ' animate-pulse' : '')
        }
      >
        {/* Ambient pulse ring on 'saved' — uses the .save-pulse-ring
            keyframe in globals.css to expand outward and fade.
            ::after positioned absolutely so the pulse doesn't
            displace the dot. */}
        {ringPulse === 'pine' && (
          <span
            aria-hidden
            className="save-pulse-ring absolute inset-0 rounded-full bg-pine"
          />
        )}
      </span>
      <span className={labelClass}>{label}</span>
    </span>
  );
}

function isStructuredContent(x: unknown): x is StructuredContent {
  return (
    typeof x === 'object' &&
    x !== null &&
    'topics' in x &&
    Array.isArray((x as { topics: unknown }).topics)
  );
}

// (ExportButton + ExportMenuSection + ExportMenuItem were the
// pre-mockup split-button export UI. Replaced by the icon-button
// .icon-btn family in the mockup-v4 editor header. Removed during
// audit-fix sweep — recoverable from git history.)

// (VariantToggle was the pre-mockup segmented control for
// templateVariant. The current editor header surfaces this through
// the meeting metadata panel directly. Removed during audit-fix
// sweep — recoverable from git history.)
